import React, { useState, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import Tesseract from 'tesseract.js';

// Configure pdf.js worker using CDN
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

const DocumentVerification = ({ file, requirementType, onVerificationComplete }) => {
  useEffect(() => {
    if (!file) return;

    const verifyDocument = async () => {
      try {
        const fileType = file.type;
        const fileExtension = file.name.split('.').pop().toLowerCase();
        const fileSize = file.size;

        // Basic file validation
        const validationResult = validateFileBasics(fileType, fileSize, fileExtension);
        if (!validationResult.valid) {
          onVerificationComplete({
            status: 'failed',
            message: validationResult.message,
            details: null,
          });
          return;
        }

        // Process based on file type
        let documentData;
        if (fileType === 'application/pdf') {
          documentData = await processPdfDocument(file);
        } else {
          documentData = await processImageDocument(file);
        }

        // Verify QVR Certificate
        const verificationResults = await verifyQVRDocument(documentData);
        onVerificationComplete({
          status: verificationResults.isValid ? 'verified' : 'failed',
          message: verificationResults.message,
          details: verificationResults.details,
        });
      } catch (error) {
        console.error('Verification error:', error);
        onVerificationComplete({
          status: 'failed',
          message: `Verification error: ${error.message}`,
          details: null,
        });
      }
    };

    verifyDocument();
  }, [file, requirementType, onVerificationComplete]);

  const validateFileBasics = (fileType, fileSize, fileExtension) => {
    const validTypes = ['image/png', 'image/jpeg', 'application/pdf'];
    if (!validTypes.includes(fileType)) {
      return { valid: false, message: 'Only PNG, JPG, or PDF files are allowed.' };
    }
    if (fileSize > 10 * 1024 * 1024) {
      return { valid: false, message: 'File size must not exceed 10MB.' };
    }
    const validExtensions = ['png', 'jpg', 'jpeg', 'pdf'];
    if (!validExtensions.includes(fileExtension)) {
      return { valid: false, message: 'Invalid file extension.' };
    }
    return { valid: true };
  };

  const processPdfDocument = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const pageCount = pdf.numPages;
    if (pageCount < 1) {
      throw new Error('PDF is empty or has no valid pages');
    }

    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1.0 });
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.height = Math.min(viewport.height, 1000); // Cap resolution
    canvas.width = Math.min(viewport.width, 1000);
    const renderViewport = page.getViewport({ scale: canvas.width / viewport.width });

    await page.render({ canvasContext: context, viewport: renderViewport }).promise;
    const { data: { text } } = await Tesseract.recognize(canvas.toDataURL(), 'eng', {
      tessedit_pageseg_mode: Tesseract.PSM.AUTO,
    });

    canvas.remove();
    return { textContent: text || '', pageCount, dimensions: { width: canvas.width, height: canvas.height } };
  };

  const processImageDocument = async (file) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    await new Promise((resolve) => (img.onload = resolve));

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = Math.min(img.width, 1000); // Cap resolution
    canvas.height = Math.min(img.height, 1000);
    context.drawImage(img, 0, 0, canvas.width, canvas.height);

    const { data: { text } } = await Tesseract.recognize(canvas.toDataURL(), 'eng', {
      tessedit_pageseg_mode: Tesseract.PSM.AUTO,
    });

    canvas.remove();
    URL.revokeObjectURL(img.src);
    return { textContent: text || '', dimensions: { width: canvas.width, height: canvas.height } };
  };

  const verifyQVRDocument = async (documentData) => {
    const { textContent, pageCount } = documentData;
    if (!textContent) {
      return {
        isValid: false,
        message: 'Document is empty or unreadable.',
        details: { error: 'No content detected' },
      };
    }

    const textLower = textContent.toLowerCase().replace(/\s+/g, ' ').trim();
    const qvrKeywords = [
      'qvr certificate', 'qualified voucher recipient', 'senior high school voucher program',
      'private education assistance committee', 'peac', 'learner reference number', 'lrn',
      'voucher application reference number', 'deped', 'department of education',
      'voucher', 'subsidy', 'senior high school', 'shs', 'automatically qualified',
      'qualified', 'school year', 'amount',
    ];
    const lrnPattern = /\b\d{12}\b/g;
    const voucherRefPattern = /\b[A-Z0-9]{6,12}\b/g;

    const keywordMatches = qvrKeywords.filter((keyword) => textLower.includes(keyword));
    const lrns = textLower.match(lrnPattern) || [];
    const voucherRefs = textLower.match(voucherRefPattern) || [];

    let confidence = Math.min(50, keywordMatches.length * 5) +
                    (lrns.length > 0 ? 20 : 0) +
                    (voucherRefs.length > 0 ? 20 : 0) +
                    Math.min(10, (pageCount || 1) * 5);

    if (confidence >= 60) {
      return {
        isValid: true,
        message: 'QVR Certificate verified successfully.',
        details: { confidence: `${confidence}%`, keywordsFound: keywordMatches.slice(0, 5), lrnsDetected: lrns.length, voucherRefsDetected: voucherRefs.length },
      };
    }
    return {
      isValid: false,
      message: 'Document does not appear to be a valid QVR Certificate. Ensure it includes required identifiers.',
      details: { confidence: `${confidence}%`, keywordsFound: keywordMatches.slice(0, 5), lrnsDetected: lrns.length, voucherRefsDetected: voucherRefs.length },
    };
  };

  return null; // No UI rendering needed
};

const VoucherDocumentVerificationSystem = () => {
  const [files, setFiles] = useState({});
  const [verificationResults, setVerificationResults] = useState({});

  const handleFileUpload = (requirementId, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFiles((prev) => ({ ...prev, [requirementId]: file }));
    setVerificationResults((prev) => ({
      ...prev,
      [requirementId]: { status: 'pending', message: 'Verification in progress...' },
    }));
  };

  const handleVerificationComplete = (requirementId, result) => {
    setVerificationResults((prev) => ({
      ...prev,
      [requirementId]: result,
    }));
  };

  const getVerificationType = (requirementName) => {
    return requirementName.toLowerCase().includes('qvr certificate') ? 'qvr_certificate' : null;
  };

  return {
    DocumentVerification,
    handleFileUpload,
    handleVerificationComplete,
    getVerificationType,
    files,
    setFiles,
    verificationResults,
    setVerificationResults,
  };
};

export default VoucherDocumentVerificationSystem;